/**
 * BottomNavigation Component
 * Mobile bottom tab bar. The role's tabs come from `getBottomNav`, which picks
 * them out of the role's `getMainNav` list (the list AppSidebar renders for
 * every role except designers, who get StudioSidebar), followed by Profile for
 * every user type. Notifications are reached from the bell in the top bar.
 */

import { User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
	getBottomNav,
	isNavPathActive,
	type NavItem,
} from "@/components/layout/nav-config";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { cn } from "@/lib/utils";

const PROFILE_TAB: NavItem = {
	label: "Profile",
	labelKey: "common.bottomNav.profile",
	icon: User,
	path: "/profile/edit",
};

function getTabsForUserType(userType?: string): NavItem[] {
	return [...getBottomNav(userType), PROFILE_TAB];
}

export function BottomNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const { user } = useAuthStore();

	const tabs = getTabsForUserType(user?.userType || undefined);

	const isActive = (path: string) => isNavPathActive(location.pathname, path);


	return (
			<nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card md:hidden">
				<div className="flex items-center justify-around">
					{tabs.map((tab) => {
						const active = isActive(tab.path);

						return (
							<button
								type="button"
								key={tab.path}
								aria-current={active ? "page" : undefined}
								onClick={() => navigate(tab.path)}
								className={cn(
									"relative flex min-w-0 flex-1 flex-col items-center gap-0.5 py-2 text-xs transition-colors",
									active
										? "text-primary"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								<div className="relative">
									<tab.icon
										className={cn("h-5 w-5", active && "stroke-[2.5]")}
									/>
								</div>
								<span
									className={cn(
										"max-w-full truncate font-medium",
										active && "font-semibold",
									)}
								>
									{tab.labelKey ? t(tab.labelKey) : tab.label}
								</span>
							</button>
						);
					})}
				</div>
			</nav>
	);
}
